import { useMemo, useState } from "react";
import { Link, createRoute } from "@tanstack/react-router";
import { facilitateRoute } from "./facilitate";
import {
  useFacilitatedRoomQuery,
  useHideFacilitatorReflectionMutation,
  useMyFacilitatedRoomScheduleQuery,
} from "@/lib/facilitator/queries";
import { useCurrentUser } from "@/lib/me";
import { StatusBadge } from "@/components/staff/StatusBadge";
import { SegmentProgress } from "@/components/staff/SegmentProgress";
import { shortDate } from "@/lib/staff/format";
import {
  SegmentSection,
  MembersSection,
  MargauxPostsSection,
  ReflectionsSection,
} from "@/components/room-detail/Sections";
import { ReflectionComposer } from "@/components/facilitator/ReflectionComposer";
import { MargauxSchedulePanel } from "@/components/facilitator/MargauxSchedulePanel";
import { NotFound } from "@/components/NotFound";
import styles from "./facilitate-room-detail.module.scss";

function FacilitateRoomDetail() {
  const { id } = facilitateRoomDetailRoute.useParams();
  const { data: room, isLoading, isError, error } = useFacilitatedRoomQuery(id);
  const { data: me } = useCurrentUser();
  const hideMutation = useHideFacilitatorReflectionMutation(id);
  const {
    data: scheduleData,
    isLoading: isScheduleLoading,
    isError: isScheduleError,
  } = useMyFacilitatedRoomScheduleQuery(id);
  const [replyTo, setReplyTo] = useState<{
    id: string;
    preview: string;
    author: string;
  } | null>(null);
  const [hideError, setHideError] = useState<string | null>(null);

  const defaultSegmentId = useMemo(() => {
    if (!room) return null;
    const current = room.segments.find(
      (s) => s.segmentNumber === room.currentSegment,
    );
    return current?.id ?? room.segments[0]?.id ?? null;
  }, [room]);

  function handleReply(reflectionId: string) {
    if (!room) return;
    const target = room.reflections.find((r) => r.id === reflectionId);
    if (!target) return;
    setReplyTo({
      id: reflectionId,
      preview: target.reflectionText.slice(0, 140),
      author: target.user.displayName ?? target.user.username ?? "Reader",
    });
  }

  async function handleHide(reflectionId: string) {
    if (!room) return;
    const target = room.reflections.find((r) => r.id === reflectionId);
    const author =
      target?.user.displayName ?? target?.user.username ?? "this reader";
    if (
      !window.confirm(
        `Hide ${author}'s reflection from the room feed? This is reversible only by an operator.`,
      )
    ) {
      return;
    }
    setHideError(null);
    try {
      await hideMutation.mutateAsync(reflectionId);
    } catch (err) {
      setHideError((err as Error)?.message ?? "Failed to hide reflection.");
    }
  }

  if (isLoading) {
    return (
      <main className={styles.shell}>
        <div className={styles.empty}>Loading room…</div>
      </main>
    );
  }
  // 404 returned by the API surfaces as `data === null`. Render NotFound so the
  // surface stays hidden from non-eligible users.
  if (room === null) return <NotFound />;
  if (isError || !room) {
    return (
      <main className={styles.shell}>
        <div className={styles.errorBanner} role="alert">
          Failed to load room: {(error as Error)?.message ?? "Unknown error"}
        </div>
      </main>
    );
  }

  return (
    <main className={styles.shell}>
      <nav className={styles.crumbs} aria-label="Breadcrumb">
        <Link to="/facilitate" className={styles.crumbLink}>
          Your rooms
        </Link>
        <span aria-hidden>/</span>
        <span>{room.name}</span>
      </nav>

      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>{room.name}</h1>
          <p className={styles.sub}>
            {room.book.title} — {room.book.author}
          </p>
          <div className={styles.statusRow}>
            <StatusBadge status={room.status} />
            <SegmentProgress
              current={room.currentSegment}
              total={room.totalSegments}
              highlightCurrent={room.status === "ACTIVE"}
            />
            <span className={styles.sub}>
              {shortDate(room.startDate)} → {shortDate(room.endDate)}
            </span>
          </div>
        </div>
      </header>

      <div className={styles.grid}>
        <div>
          <SegmentSection
            segments={room.segments}
            currentSegment={room.currentSegment}
            status={room.status}
          />
          {room.status !== "COMPLETED" &&
          room.status !== "CANCELLED" &&
          room.segments.length > 0 ? (
            <ReflectionComposer
              roomId={room.id}
              segments={room.segments}
              defaultSegmentId={defaultSegmentId}
              replyTo={replyTo}
              onCancelReply={() => setReplyTo(null)}
            />
          ) : null}
          <MargauxPostsSection posts={room.margauxPosts} />
          {hideError ? (
            <div className={styles.errorBanner} role="alert">
              {hideError}
            </div>
          ) : null}
          <ReflectionsSection
            reflections={room.reflections}
            onReply={
              room.status !== "COMPLETED" && room.status !== "CANCELLED"
                ? handleReply
                : undefined
            }
            onHide={
              room.status !== "COMPLETED" && room.status !== "CANCELLED"
                ? handleHide
                : undefined
            }
            hidingReflectionId={
              hideMutation.isPending ? (hideMutation.variables ?? null) : null
            }
            hideableForUserId={me?.id ?? null}
          />
        </div>
        <div>
          <MembersSection members={room.members} />
          <MargauxSchedulePanel
            items={scheduleData?.items ?? []}
            isLoading={isScheduleLoading}
            isError={isScheduleError}
          />
        </div>
      </div>
    </main>
  );
}

export const facilitateRoomDetailRoute = createRoute({
  getParentRoute: () => facilitateRoute,
  path: "/$id",
  component: FacilitateRoomDetail,
});
